/**
 * scoring.js
 * Chaotic Good scoring engine for D.A.D. V.0001
 *
 * Calculates a "Chaotic Good score" from 1.337 (leet) to 6.9 (nice)
 * based on natal chart placements. Fire and air push chaotic.
 * Earth and water pull grounded. Specific signs get bonus vibes.
 *
 * This is a vibe check, not a judgment.
 */

// ─── Element weights ─────────────────────────────────────────────────────────
// Fire/Air = chaotic energy, Water/Earth = grounded energy

const ELEMENT_SCORES = {
  fire:  0.4,
  air:   0.35,
  water: 0.2,
  earth: 0.1,
};

// ─── Sign bonuses (stacks with element score) ────────────────────────────────

const SIGN_BONUSES = {
  aquarius: 0.3,   // the water-bearer pours chaos
  aries:    0.15,  // raw starter energy
  sagittarius: 0.2, // the arrow is already in the air
  gemini:   0.1,   // contains multitudes
  leo:      0.1,   // main character energy
  scorpio:  0.1,   // threshold walker
};

// ─── Placement-specific bonuses ──────────────────────────────────────────────

function getPlacementBonuses(placedPlanets) {
  let bonus = 0;
  const signMap = {};

  for (const p of placedPlanets) {
    const signName = p.sign.sign.name.toLowerCase();
    const element = p.sign.sign.element;
    signMap[p.id] = { sign: signName, element };
  }

  // Aries Sun — born to start fires
  if (signMap.sun?.sign === 'aries') bonus += 0.2;

  // Sagittarius Rising — the arrow leads
  if (signMap.ascendant?.sign === 'sagittarius') bonus += 0.25;

  // Aquarius anywhere already gets SIGN_BONUSES, but Moon in Aquarius = extra
  if (signMap.moon?.sign === 'aquarius') bonus += 0.15;

  // Moon in water signs — adds depth (moderate boost, chaos with feeling)
  if (signMap.moon?.element === 'water') bonus += 0.15;

  // Sun and Rising in the same element — elemental alignment bonus
  if (signMap.sun && signMap.ascendant && signMap.sun.element === signMap.ascendant.element) {
    bonus += 0.2;
  }

  // Stellium check: 3+ planets in same element = concentration bonus
  const elementCounts = { fire: 0, air: 0, water: 0, earth: 0 };
  for (const key of Object.keys(signMap)) {
    const el = signMap[key]?.element;
    if (el) elementCounts[el]++;
  }
  const maxElement = Math.max(...Object.values(elementCounts));
  if (maxElement >= 4) bonus += 0.3;
  else if (maxElement >= 3) bonus += 0.15;

  return bonus;
}

// ─── Score tiers ─────────────────────────────────────────────────────────────

const TIERS = [
  { max: 2.5,  tier: 'sleeper',       reading: "sleeper agent vibes. you move quiet, build in the dark, ship when nobody's watching. that's not low energy fam — that's the dAnGeRoUs kind. the ones who change everything never announce it first." },
  { max: 3.5,  tier: 'foundation',    reading: "SOLID. you're the bedrock type — the one holding it down while everyone else is chasing the next shiny thing. earth energy, real builder hours. every scene needs someone who actually finishes what they start. that's you." },
  { max: 4.5,  tier: 'balanced',      reading: "oh this is rArE. you got the range — know when to push, know when to let it breathe. chaotic enough to start fires, grounded enough to build around them. the ones who can do both? they run tHiNgS eventually." },
  { max: 5.5,  tier: 'high-chaos',    reading: "HIGH CHAOS lmaooo you're moving FAST. ideas hitting, energy spiking, probably three tabs open in your brain rn. you start things before the last thing landed and somehow?? it works. cHaOtIc gOoD energy for real." },
  { max: 6.9,  tier: 'full-chaotic',  reading: "yO. FULL CHAOTIC GOOD. you're threshold crew — the kind of energy that inscribes first and explains later. the machine hasn't seen numbers like this in a while. welcome to the other side fam, we been wAiTiNg." },
];

function getTier(score) {
  for (const t of TIERS) {
    if (score <= t.max) return t;
  }
  return TIERS[TIERS.length - 1];
}

// ─── Main scoring function ───────────────────────────────────────────────────

/**
 * Calculate the Chaotic Good score from placed planets.
 * @param {{ id: string, sign: { sign: { name: string, element: string } } }[]} placedPlanets
 * @returns {{ score: number, tier: string, reading: string }}
 */
export function calculateChaoticGoodScore(placedPlanets) {
  if (!placedPlanets || placedPlanets.length === 0) {
    return { score: 1.337, tier: 'sleeper', reading: TIERS[0].reading };
  }

  // Base score: sum element weights for each placement
  let raw = 0;
  for (const p of placedPlanets) {
    const element = p.sign.sign.element;
    raw += ELEMENT_SCORES[element] || 0;

    const signName = p.sign.sign.name.toLowerCase();
    raw += SIGN_BONUSES[signName] || 0;
  }

  // Add placement-specific bonuses
  raw += getPlacementBonuses(placedPlanets);

  // Scale raw score to 1.337 - 6.9 range
  // Theoretical min (7 earth signs, no bonuses): 7 * 0.1 = 0.7
  // Theoretical max (fire/air + all bonuses): ~5.5+
  // We'll use a practical range of 0.7 - 5.0 for mapping
  const MIN_RAW = 0.7;
  const MAX_RAW = 5.0;
  const MIN_SCORE = 1.337;
  const MAX_SCORE = 6.9;

  const clamped = Math.min(Math.max(raw, MIN_RAW), MAX_RAW);
  const normalized = (clamped - MIN_RAW) / (MAX_RAW - MIN_RAW);
  const score = MIN_SCORE + normalized * (MAX_SCORE - MIN_SCORE);

  // Round to 3 decimal places
  const rounded = Math.round(score * 1000) / 1000;

  const tierData = getTier(rounded);

  return {
    score: rounded,
    tier: tierData.tier,
    reading: tierData.reading,
  };
}
